/**
 * Checking a route against the charted depths actually on screen.
 *
 * This is the piece that makes the safety work protect a passage rather than
 * just a viewport. `routeProbes` in core/route.ts produces the positions to
 * test; this module does the testing, and it lives here rather than in core
 * because it must read back the vector tiles MapLibre has loaded.
 *
 * IMPORTANT LIMITATION, and the reason `coverage` is reported separately:
 * `queryRenderedFeatures` only sees tiles that are **currently loaded for the
 * current viewport**. A leg outside the view, or in an area whose tiles have not
 * arrived, cannot be checked. Reporting "no warnings" for such a leg would be a
 * lie of exactly the kind this app exists to avoid, so the result distinguishes
 * "checked and clear" from "could not check".
 */

import type { Map as MapLibreMap, MapGeoJSONFeature } from 'maplibre-gl';

import {
  type BoatProfile,
  isUnsafeClearance,
  isUnsafeDepth,
  safetyDepth,
} from '../core/boat';
import {
  DEPTH_CHECK_SPACING_M,
  type Route,
  type RouteWarning,
  routeProbes,
  sortWarnings,
} from '../core/route';
import { formatDepth } from '../core/units';
import type { DepthUnit } from '../core/units';
import { type ChartManifest, chartSourceId } from './style';
import { chartedDepthAt } from './chartDepth';

/** How close to a probe a hazard must be to count as on the route, pixels. */
const HAZARD_QUERY_RADIUS_PX = 12;

export interface RouteCheckResult {
  warnings: RouteWarning[];
  /** Probes that were tested against loaded tiles. */
  probesChecked: number;
  /** Probes whose tiles were not loaded, so nothing could be said about them. */
  probesUnchecked: number;
  /** True when every probe was checked — only then is "clear" meaningful. */
  complete: boolean;
}

interface HazardProps {
  kind?: string;
  valsou?: number;
  watlev?: number;
  objnam?: string;
}

interface ClearanceProps {
  kind?: string;
  verclr?: number;
  objnam?: string;
}

/**
 * Layer ids to query, largest scale first so the best survey wins.
 * Built with `chartSourceId` rather than by hand: two places inventing the same
 * id independently is how a layer silently stops being checked.
 */
const layerIdsFor = (manifest: ChartManifest, suffix: string): string[] =>
  [...manifest.sources]
    .sort((a, b) => b.band - a.band)
    .map((source) => `${chartSourceId(source)}-${suffix}`);

/**
 * Check every leg of a route against the charted depths, hazards and overhead
 * clearances currently loaded.
 *
 * The caller is responsible for having the route in view; see
 * `fitRouteForChecking` for why that matters.
 */
export function checkRouteAgainstChart(
  map: MapLibreMap,
  route: Pick<Route, 'waypoints'>,
  boat: BoatProfile,
  manifest: ChartManifest,
  depthUnit: DepthUnit,
  spacingM: number = DEPTH_CHECK_SPACING_M,
): RouteCheckResult {
  const probes = routeProbes(route, spacingM);
  const warnings: RouteWarning[] = [];
  let checked = 0;
  let unchecked = 0;

  const depthLayers = layerIdsFor(manifest, 'depth-area').filter((id) => map.getLayer(id));
  const hazardLayers = [
    ...layerIdsFor(manifest, 'hazard-danger'),
    ...layerIdsFor(manifest, 'hazard-shallow'),
  ].filter((id) => map.getLayer(id));
  const clearanceLayers = layerIdsFor(manifest, 'clearance-danger').filter((id) => map.getLayer(id));

  // One warning per distinct hazard, not one per probe that happens to be near
  // it — a 50 m probe spacing would otherwise report the same rock repeatedly.
  const reportedHazards = new Set<string>();
  const shallowLegs = new Set<number>();

  for (const probe of probes) {
    const point = map.project([probe.position.lon, probe.position.lat]);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
      unchecked += 1;
      continue;
    }

    const depthFeatures = depthLayers.length
      ? map.queryRenderedFeatures(point, { layers: depthLayers })
      : [];

    // No depth area under the probe means the tile is not loaded, or the chart
    // genuinely has no coverage there. Either way nothing can be concluded.
    if (depthFeatures.length === 0) {
      unchecked += 1;
      continue;
    }
    checked += 1;

    // The largest-scale chart covering the probe is the answer — not the
    // shallowest across bands, which reads "zero metres" wherever a general
    // chart lumps 0–18.3 m into one polygon, and reported deep harbour water as
    // shoal. See chartDepth.ts.
    const shallowest = chartedDepthAt(depthFeatures)?.depthM ?? null;

    if (isUnsafeDepth(shallowest, boat) && !shallowLegs.has(probe.legIndex)) {
      shallowLegs.add(probe.legIndex);
      warnings.push({
        kind: 'shallow',
        legIndex: probe.legIndex,
        position: probe.position,
        distanceAlongRouteM: probe.distanceAlongRouteM,
        severity: 'danger',
        message: shallowest === null
          ? `Leg ${probe.legIndex + 1} crosses water with no charted depth`
          : `Leg ${probe.legIndex + 1} crosses ${formatDepth(shallowest, depthUnit)}`
            + ` — less than your ${formatDepth(safetyDepth(boat), depthUnit)} safety depth`,
      });
    }

    for (const feature of queryAround(map, point, hazardLayers)) {
      const props = feature.properties as HazardProps;
      const key = hazardKey(feature);
      if (reportedHazards.has(key)) continue;
      reportedHazards.add(key);

      const depthText = typeof props.valsou === 'number'
        ? formatDepth(props.valsou, depthUnit)
        : 'unsurveyed depth';
      warnings.push({
        kind: 'hazard',
        legIndex: probe.legIndex,
        position: probe.position,
        distanceAlongRouteM: probe.distanceAlongRouteM,
        severity: 'danger',
        message: `${describeKind(props.kind)} on leg ${probe.legIndex + 1} (${depthText})`
          + (props.objnam ? ` — ${props.objnam}` : ''),
      });
    }

    for (const feature of queryAround(map, point, clearanceLayers)) {
      const props = feature.properties as ClearanceProps;
      const key = `clearance:${hazardKey(feature)}`;
      if (reportedHazards.has(key)) continue;
      if (!isUnsafeClearance(props.verclr ?? null, boat)) continue;
      reportedHazards.add(key);

      warnings.push({
        kind: 'low_clearance',
        legIndex: probe.legIndex,
        position: probe.position,
        distanceAlongRouteM: probe.distanceAlongRouteM,
        severity: 'danger',
        message: `${describeKind(props.kind)} on leg ${probe.legIndex + 1}`
          + (typeof props.verclr === 'number'
            ? ` — ${formatDepth(props.verclr, depthUnit)} clearance`
            : ' — clearance not charted'),
      });
    }
  }

  return {
    warnings: sortWarnings(warnings),
    probesChecked: checked,
    probesUnchecked: unchecked,
    complete: unchecked === 0 && checked > 0,
  };
}

function queryAround(
  map: MapLibreMap,
  point: { x: number; y: number },
  layers: string[],
): MapGeoJSONFeature[] {
  if (!layers.length) return [];
  const r = HAZARD_QUERY_RADIUS_PX;
  return map.queryRenderedFeatures(
    [[point.x - r, point.y - r], [point.x + r, point.y + r]],
    { layers },
  );
}

/**
 * Identify a hazard across probes and bands. The same rock is charted in every
 * band that covers it, and several probes pass near it; without this the same
 * danger is reported many times over.
 */
function hazardKey(feature: MapGeoJSONFeature): string {
  const p = feature.properties as Record<string, unknown>;
  return [p.kind, p.objnam ?? '', p.valsou ?? '', p.watlev ?? ''].join('|');
}

function describeKind(kind: string | undefined): string {
  const labels: Record<string, string> = {
    rock: 'Rock',
    wreck: 'Wreck',
    obstruction: 'Obstruction',
    bridge: 'Bridge',
    overhead_cable: 'Overhead cable',
    pylon: 'Pylon',
  };
  return (kind && labels[kind]) || 'Charted danger';
}

/**
 * Whether the whole route is inside the current view.
 *
 * A route can only be checked where tiles are loaded, and tiles are only loaded
 * for what is on screen. The UI uses this to fit the route to the view before
 * checking, and to explain the result honestly when it cannot.
 */
export function routeIsFullyInView(map: MapLibreMap, route: Pick<Route, 'waypoints'>): boolean {
  if (route.waypoints.length === 0) return false;
  const bounds = map.getBounds();
  return route.waypoints.every((waypoint) => bounds.contains([waypoint.lon, waypoint.lat]));
}
